import { CheckCircle } from "lucide-react"
import type { HowItWorksStep } from "../types"

interface HowItWorksSectionProps {
  /** Steps to display */
  steps: HowItWorksStep[]
}

/**
 * How It Works section showing numbered steps.
 * Displays the implementation process for the use case.
 */
export function HowItWorksSection({ steps }: HowItWorksSectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-neutral-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-2xl font-bold text-neutral-900 sm:text-3xl">
            How It Works
          </h2>
          <p className="mx-auto max-w-2xl text-neutral-600">
            Get up and running in just a few steps. Our platform makes it easy to
            deploy and customize AI employees for your specific needs.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => {
            const Icon = step.icon || CheckCircle
            return (
              <div
                key={step.step}
                className="relative rounded-2xl bg-white p-6 shadow-sm ring-1 ring-neutral-200/80"
              >
                {/* Step Number */}
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white font-bold text-sm">
                    {step.step}
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-sm leading-relaxed text-neutral-600">
                  {step.description}
                </p>

                {/* Connector Line (not on last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 -right-3 w-6 h-0.5 bg-neutral-200" />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
